import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  FolderKanban,
  Keyboard,
  Moon,
  MousePointerClick,
  PanelLeft,
  Receipt,
  Search,
  ShoppingCart,
  Sparkles,
  Sun,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { Permission, SearchHit } from '@proerp/shared';
import {
  Badge,
  Button,
  cn,
  Command,
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Skeleton,
  Spinner,
  useSidebar,
} from '@proerp/ui';
import { formatDate } from '@/lib/format';
import { projectRefLabel } from '@/components/ProjectRef';
import { useProjectRefs } from '@/lib/useProjectRefs';
import { formatMoney } from '@/lib/money';
import { useArticle } from '@/features/inventory/articlesApi';
import { unitLabel } from '@/features/inventory/articleMeta';
import { useCustomer } from '@/features/crm/customersApi';
import { useProject } from '@/features/projects/projectsApi';
import { useSalesDocument } from '@/features/sales/salesApi';
import { usePurchaseOrder } from '@/features/procurement/procurementApi';
import { useNavItems } from '@/components/useNavItems';
import { useTheme } from '@/lib/theme';
import { usePermissions } from '@/lib/useCurrentUser';
import { useUiStore } from '@/store/uiStore';
import { featureRoute, useSearch } from './searchApi';
import {
  countHits,
  formatSimilarity,
  isSimilarHit,
  RESOURCE_ICON,
  RESOURCE_LABEL,
  splitSearchGroups,
  type SearchResourceTypeUi,
} from './searchMeta';
import { renderSnippet } from './searchSnippet';

/** Ein Befehl in der Palette (Sprung/Umschalter/Schnellaktion) — kein Suchtreffer. */
interface PaletteCommand {
  id: string;
  label: string;
  hint?: string;
  keywords?: string;
  icon: LucideIcon;
  group: 'Aktionen' | 'Navigation' | 'Ansicht';
  run: () => void;
}

/**
 * Schnellaktionen: springen auf die Seite und öffnen dort per `?neu=1` den
 * Anlegen-Dialog (siehe `useOpenOnParam`). Nur sichtbar mit dem passenden
 * Anlege-Recht.
 */
const ACTION_COMMANDS: {
  id: string;
  label: string;
  keywords: string;
  icon: LucideIcon;
  route: string;
  permission: Permission;
}[] = [
  { id: 'action:project', label: 'Neues Projekt', keywords: 'projekt anlegen neu', icon: FolderKanban, route: '/projekte?neu=1', permission: 'projects.create' },
  { id: 'action:sales', label: 'Beleg anlegen (Verkauf)', keywords: 'beleg angebot rechnung auftrag verkauf neu', icon: Receipt, route: '/verkauf?neu=1', permission: 'sales.create' },
  { id: 'action:customer', label: 'Neuer Kunde / Lieferant', keywords: 'kunde lieferant kontakt crm neu', icon: Users, route: '/crm?neu=1', permission: 'crm.create' },
  { id: 'action:purchase', label: 'Neue Bestellung (Einkauf)', keywords: 'bestellung einkauf anfrage lieferant neu', icon: ShoppingCart, route: '/einkauf?neu=1', permission: 'procurement.create' },
];

const num = (value: number) => new Intl.NumberFormat('de-AT', { maximumFractionDigits: 3 }).format(value);

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  CUSTOMER: 'Kunde',
  PROSPECT: 'Interessent',
  SUPPLIER: 'Lieferant',
};

/** cmdk-Werte der Sonderzeilen (kollidieren nie mit `type-id`-Werten). */
const ALL_RESULTS_VALUE = '__all-results__';
const MORE_GROUPS_VALUE = '__more-groups__';
const MORE_SIMILAR_VALUE = '__more-similar__';

/**
 * Obergrenzen der Liste. Die Suche kennt inzwischen rund 30 Ressourcentypen
 * (Tech-Lead 2026-08-12: „wirklich alles durchsuchen") — ohne Deckel stünden bei
 * einem allgemeinen Begriff 30 Überschriften und ein paar hundert Zeilen im
 * Fenster, durch die man sich mit den Pfeiltasten durchhangeln müsste. Deshalb
 * zeigt die Palette erst einen Ausschnitt und klappt auf Wunsch (Zeile am Ende
 * des Abschnitts, mit Enter erreichbar) den Rest auf.
 */
const VISIBLE_CHIP_LIMIT = 10;
const VISIBLE_GROUP_LIMIT = 8;
const VISIBLE_SIMILAR_LIMIT = 6;

/** Zähl-Text mit deutscher Ein-/Mehrzahl („1 Kategorie" / „3 Kategorien"). */
function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Eindeutiger cmdk-Wert eines Treffers — dient zugleich als Vorschau-Schlüssel. */
function hitValue(hit: SearchHit): string {
  return `${hit.type}-${hit.id}`;
}

/** Debounce für die Eingabe — vermeidet eine Anfrage pro Tastenanschlag. */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Zentrierte Status-/Leerzustands-Fläche in der Trefferliste. */
function ListStatus({ children, tone }: { children: React.ReactNode; tone?: 'danger' }) {
  return (
    <div
      className={`flex min-h-[40vh] flex-col items-center justify-center gap-2 px-6 text-center text-sm ${
        tone === 'danger' ? 'text-destructive' : 'text-muted-foreground'
      }`}
    >
      {children}
    </div>
  );
}

/** Tasten-Hinweis im Fuß (kbd-Optik). */
function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[1.4rem] items-center justify-center rounded border border-border bg-muted px-1 py-0.5 font-sans text-[0.6875rem] text-muted-foreground">
      {children}
    </kbd>
  );
}

/** Gemeinsame Optik der Pillen unter dem Suchfeld. */
const CHIP_BASE =
  'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50';
const CHIP_IDLE = 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground';

/**
 * Enter/Leertaste auf einer fokussierten Pille schaltet die Pille — und darf
 * nicht zu cmdk durchsickern (Enter würde sonst den markierten Treffer öffnen).
 */
function chipKeyDown(onSelect: () => void) {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onSelect();
    }
  };
}

/**
 * Kategorie-Chip (Variante B): Pill-Button unter dem Suchfeld, filtert die
 * Trefferliste client-seitig nach Ressourcentyp. Aktiv = Primärfarbe.
 */
function FilterChip({
  label,
  count,
  isActive,
  onSelect,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onSelect}
      onKeyDown={chipKeyDown(onSelect)}
      className={cn(
        CHIP_BASE,
        isActive ? 'border-transparent bg-primary text-primary-foreground' : CHIP_IDLE,
      )}
    >
      {label}
      <span className={cn('tabular-nums', isActive ? 'text-primary-foreground/70' : 'text-muted-foreground/70')}>
        {count}
      </span>
    </button>
  );
}

/**
 * „+N weitere" — klappt die restlichen Kategorie-Chips auf. Nötig, seit die
 * Suche rund 30 Typen kennt: sonst wächst die Chip-Leiste über den halben
 * Dialog und schiebt die Treffer aus dem Bild.
 */
function MoreChipsButton({ count, onSelect }: { count: number; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onKeyDown={chipKeyDown(onSelect)}
      className={cn(CHIP_BASE, CHIP_IDLE)}
    >
      +{count} weitere
    </button>
  );
}

/** Kennzeichnung eines nur ähnlich geschriebenen Treffers in der Trefferliste. */
function SimilarBadge({ hit }: { hit: SearchHit }) {
  const percent = formatSimilarity(hit);
  return (
    <Badge
      tone="outline"
      title={
        percent
          ? `Ähnlichkeit ${percent} — andere Schreibweise oder Tippfehler`
          : 'Ähnlich geschrieben — andere Schreibweise oder Tippfehler'
      }
    >
      ähnlich{percent ? ` · ${percent}` : ''}
    </Badge>
  );
}

/** Grundgerüst der rechten Vorschau-Spalte (Variante C): Eyebrow, Icon-Kachel, Inhalt, Aktion. */
function PreviewFrame({
  eyebrow,
  icon,
  children,
  actionLabel,
  onAction,
  route,
}: {
  eyebrow: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  actionLabel: string;
  onAction: () => void;
  route: string;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-6">
      <span className="text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground">{eyebrow}</span>
      <span className="mt-4 flex size-14 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        {icon}
      </span>
      {children}
      <div className="mt-auto pt-6">
        <Button variant="primary" className="w-full" onClick={onAction}>
          {actionLabel} <span aria-hidden>↵</span>
        </Button>
        {/* Ziel-Route als dezenter Hinweis unter der Aktion. */}
        <p className="mt-2 truncate text-center text-xs text-muted-foreground" title={route}>
          {route}
        </p>
      </div>
    </div>
  );
}

/** Eine Zeile „Label — Wert" in der erweiterten Vorschau; blendet sich bei leerem Wert aus. */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1.5 text-sm last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="mt-4 flex flex-col gap-2">
      {[0, 1, 2].map((key) => (
        <Skeleton key={key} className="h-5 w-full" />
      ))}
    </div>
  );
}

/**
 * Zusätzliche Detailfelder je Ressourcentyp — auf Abruf geladen (echter, aktueller
 * Datensatz statt Such-Index-Snapshot). Nur für die häufigsten Treffertypen; andere
 * Typen zeigen weiterhin nur Titel/Untertitel/Badge/Fundstelle.
 */
function ArticleExtraDetails({ id }: { id: string }) {
  const { data, isLoading } = useArticle(id);
  const article = data?.data;
  if (isLoading) return <DetailSkeleton />;
  if (!article) return null;
  return (
    <div className="mt-4 flex flex-col">
      <DetailRow label="Verkaufspreis" value={article.salesPrice ? formatMoney(article.salesPrice) : '—'} />
      <DetailRow label="Lagerbestand" value={`${num(article.stockQty)} ${unitLabel(article.unit)}`} />
      <DetailRow label="Verfügbar" value={`${num(article.available)} ${unitLabel(article.unit)}`} />
      <DetailRow label="Hersteller" value={article.manufacturer} />
      {!article.isActive && <DetailRow label="Status" value="Inaktiv" />}
    </div>
  );
}

function CustomerExtraDetails({ id }: { id: string }) {
  const { data, isLoading } = useCustomer(id);
  const customer = data?.data;
  if (isLoading) return <DetailSkeleton />;
  if (!customer) return null;
  return (
    <div className="mt-4 flex flex-col">
      <DetailRow label="Typ" value={CUSTOMER_TYPE_LABELS[customer.type] ?? customer.type} />
      <DetailRow label="E-Mail" value={customer.email} />
      <DetailRow label="Telefon" value={customer.phone} />
      <DetailRow label="Ort" value={[customer.postalCode, customer.city].filter(Boolean).join(' ') || null} />
      <DetailRow label="Zahlungsziel" value={customer.paymentTermsDays != null ? `${customer.paymentTermsDays} Tage` : null} />
    </div>
  );
}

function SalesDocumentExtraDetails({ id }: { id: string }) {
  const { data, isLoading } = useSalesDocument(id);
  const projectRefs = useProjectRefs();
  const doc = data?.data;
  if (isLoading) return <DetailSkeleton />;
  if (!doc) return null;
  return (
    <div className="mt-4 flex flex-col">
      <DetailRow label="Kunde" value={doc.customerName} />
      <DetailRow label="Datum" value={formatDate(doc.documentDate)} />
      <DetailRow label="Netto" value={formatMoney(doc.net)} />
      <DetailRow label="Brutto" value={formatMoney(doc.gross)} />
      <DetailRow
        label="Projekt"
        value={projectRefLabel(doc.projectCode, doc.projectId ? projectRefs.get(doc.projectId)?.name : null) || null}
      />
    </div>
  );
}

function PurchaseOrderExtraDetails({ id }: { id: string }) {
  const { data, isLoading } = usePurchaseOrder(id);
  const order = data?.data;
  if (isLoading) return <DetailSkeleton />;
  if (!order) return null;
  return (
    <div className="mt-4 flex flex-col">
      <DetailRow label="Lieferant" value={order.supplierName} />
      <DetailRow label="Datum" value={formatDate(order.orderDate)} />
      <DetailRow label="Netto" value={formatMoney(order.net)} />
      <DetailRow label="Brutto" value={formatMoney(order.gross)} />
      <DetailRow label="Erwartet" value={order.expectedDate ? formatDate(order.expectedDate) : null} />
    </div>
  );
}

function ProjectExtraDetails({ id }: { id: string }) {
  const { data, isLoading } = useProject(id);
  const project = data?.data;
  if (isLoading) return <DetailSkeleton />;
  if (!project) return null;
  return (
    <div className="mt-4 flex flex-col">
      <DetailRow label="Kunde" value={project.customerName} />
      <DetailRow label="Projektleitung" value={project.projectManager} />
      <DetailRow label="Ort" value={project.location} />
      <DetailRow label="Auftragswert" value={project.orderValue ? formatMoney(project.orderValue) : null} />
    </div>
  );
}

/** Wählt die passenden Zusatzdetails je Treffertyp — `null` für nicht abgedeckte Typen. */
function HitExtraDetails({ hit }: { hit: SearchHit }) {
  switch (hit.type) {
    case 'article':
      return <ArticleExtraDetails id={hit.id} />;
    case 'customer':
      return <CustomerExtraDetails id={hit.id} />;
    case 'sales-document':
      return <SalesDocumentExtraDetails id={hit.id} />;
    case 'purchase-order':
      return <PurchaseOrderExtraDetails id={hit.id} />;
    case 'project':
      return <ProjectExtraDetails id={hit.id} />;
    default:
      return null;
  }
}

/** Detailvorschau des markierten Treffers. */
function HitPreview({ hit, groupLabel, onOpen }: { hit: SearchHit; groupLabel: string; onOpen: () => void }) {
  const Icon = RESOURCE_ICON[hit.type];
  return (
    <PreviewFrame
      eyebrow={groupLabel}
      icon={<Icon className="size-7" strokeWidth={1.5} aria-hidden />}
      actionLabel="Öffnen"
      onAction={onOpen}
      route={featureRoute(hit.type, hit.id)}
    >
      <span className="mt-4 break-words text-lg font-semibold tabular-nums text-foreground">{hit.title}</span>
      {hit.subtitle && <span className="mt-1 text-sm text-muted-foreground">{hit.subtitle}</span>}
      {hit.badge && (
        <span className="mt-3">
          <Badge>{hit.badge}</Badge>
        </span>
      )}
      {/* Die Vorschau zeigt einen Treffer ohne seinen Abschnitt — deshalb steht
          hier noch einmal, dass es nur ein ähnlicher Treffer ist. */}
      {isSimilarHit(hit) && (
        <span className="mt-3 flex flex-col items-start gap-1">
          <Badge tone="outline">
            Ähnlicher Treffer{formatSimilarity(hit) ? ` · ${formatSimilarity(hit)}` : ''}
          </Badge>
          <span className="text-xs text-muted-foreground">
            Nicht genau dein Suchbegriff, nur ähnlich geschrieben.
          </span>
        </span>
      )}
      {hit.snippet && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">…{renderSnippet(hit.snippet)}…</p>
      )}
      <HitExtraDetails hit={hit} />
    </PreviewFrame>
  );
}

/** Vorschau der „Alle Ergebnisse"-Aktion. */
function AllResultsPreview({ query, onOpen }: { query: string; onOpen: () => void }) {
  return (
    <PreviewFrame
      eyebrow="Suche"
      icon={<ArrowRight className="size-7" strokeWidth={1.5} aria-hidden />}
      actionLabel="Öffnen"
      onAction={onOpen}
      route={`/suche?q=${encodeURIComponent(query)}`}
    >
      <span className="mt-4 break-words text-lg font-semibold text-foreground">
        Alle Ergebnisse für „{query}"
      </span>
      <span className="mt-1 text-sm text-muted-foreground">
        Öffnet die Ergebnisseite mit allen Treffern aller Module.
      </span>
    </PreviewFrame>
  );
}

/** Platzhalter der Vorschau-Spalte, solange kein Treffer markiert ist. */
function PreviewPlaceholder() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
      <span className="flex size-12 items-center justify-center rounded-full bg-muted">
        <MousePointerClick className="size-5" strokeWidth={1.5} aria-hidden />
      </span>
      Treffer auswählen für Details
    </div>
  );
}

/**
 * Universelle Suche als Command-Palette (ADR 0015) auf cmdk-Basis. ⌘K/Strg+K
 * öffnet sie (in der App-Shell verdrahtet); Pfeiltasten navigieren, Enter
 * springt zum Datensatz, Esc schließt. Das Filtern übernimmt der Server
 * (`shouldFilter={false}`). Großes Overlay (~2/3 des Fensters), Lade-/Fehler-/
 * Leerzustände sind explizit.
 *
 * Layout (bestätigtes Design, Mix aus Variante B + C):
 * - Kategorie-Chips unter dem Suchfeld filtern die Gruppen client-seitig.
 * - Ab `md`: geteiltes Layout — links Trefferliste, rechts Detailvorschau des
 *   markierten Eintrags (cmdk-Selektion kontrolliert via `value`/`onValueChange`).
 *
 * Reihenfolge der Liste (Tech-Lead 2026-08-12, „smarte Suche"):
 * 1. genaue Treffer, nach Typ gruppiert (Gruppen mit Kennungs-Treffer zuerst),
 * 2. Zeile „… weitere Kategorien anzeigen", wenn mehr als `VISIBLE_GROUP_LIMIT`
 *    Gruppen anstehen,
 * 3. Abschnitt „Ähnliche Treffer" — flach, nach Ähnlichkeit sortiert, jede Zeile
 *    mit „ähnlich"-Kennzeichen,
 * 4. „Alle Ergebnisse anzeigen" (Ergebnisseite).
 */
/**
 * Präfix, das die Palette vom Suchen aufs Befehle-Filtern umschaltet
 * (Tech-Lead 2026-08-04: `#` statt des früheren `>`). Bewusst EINE Konstante —
 * das Zeichen stand vorher an vier Stellen (Logik, Platzhalter, Fußzeile,
 * Kürzel-Übersicht) und lief so leicht auseinander.
 */
export const COMMAND_PREFIX = '#';

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 200);
  // Aktiver Kategorie-Filter ('all' = kein Filter) + kontrollierte cmdk-Markierung.
  const [activeType, setActiveType] = useState<SearchResourceTypeUi | 'all'>('all');
  const [selectedValue, setSelectedValue] = useState('');
  // Aufgeklappte Abschnitte (siehe VISIBLE_*_LIMIT).
  const [areAllChipsShown, setAreAllChipsShown] = useState(false);
  const [areAllGroupsShown, setAreAllGroupsShown] = useState(false);
  const [areAllSimilarShown, setAreAllSimilarShown] = useState(false);

  // Befehls-Modus: leere Eingabe oder führendes `COMMAND_PREFIX` → Befehle statt Suche.
  const trimmedQuery = query.trim();
  const commandMode = trimmedQuery.length === 0 || trimmedQuery.startsWith(COMMAND_PREFIX);
  const commandFilter = trimmedQuery.startsWith(COMMAND_PREFIX)
    ? trimmedQuery.slice(COMMAND_PREFIX.length).trim().toLowerCase()
    : '';

  // Im Befehls-Modus keine Suchanfrage stellen.
  const { data, isLoading, isError } = useSearch(open && !commandMode ? debounced : '');

  // Befehle: Sprünge (aus der berechtigungsgefilterten Navigation) + Ansicht-Umschalter.
  const navItems = useNavItems();
  const { can } = usePermissions();
  const theme = useTheme((state) => state.theme);
  const toggleTheme = useTheme((state) => state.toggle);
  const { toggleSidebar } = useSidebar();
  const setShortcutsOpen = useUiStore((state) => state.setShortcutsOpen);

  const commands = useMemo<PaletteCommand[]>(() => {
    const list: PaletteCommand[] = [];
    for (const action of ACTION_COMMANDS) {
      if (!can(action.permission)) continue;
      list.push({ id: action.id, label: action.label, keywords: action.keywords, hint: 'Anlegen', icon: action.icon, group: 'Aktionen', run: () => navigate(action.route) });
    }
    for (const item of navItems) {
      list.push({ id: `nav:${item.to}`, label: item.label, hint: item.group, icon: item.icon, group: 'Navigation', run: () => navigate(item.to) });
      for (const child of item.children ?? []) {
        list.push({
          id: `nav:${child.to}:${child.label}`,
          label: `${item.label} › ${child.label}`,
          keywords: `${item.label} ${child.label}`.toLowerCase(),
          hint: item.group,
          icon: item.icon,
          group: 'Navigation',
          run: () => navigate(child.to),
        });
      }
    }
    list.push({ id: 'view:theme', label: theme === 'dark' ? 'Helles Design' : 'Dunkles Design', keywords: 'theme design hell dunkel erscheinungsbild dark light', icon: theme === 'dark' ? Sun : Moon, group: 'Ansicht', run: toggleTheme });
    list.push({ id: 'view:sidebar', label: 'Seitenleiste ein-/ausklappen', keywords: 'sidebar seitenleiste menu navigation', icon: PanelLeft, group: 'Ansicht', run: toggleSidebar });
    list.push({ id: 'view:shortcuts', label: 'Tastenkürzel anzeigen', keywords: 'tastenkuerzel shortcuts hotkeys tastatur hilfe', icon: Keyboard, group: 'Ansicht', run: () => setShortcutsOpen(true) });
    return list;
  }, [navItems, can, theme, toggleTheme, toggleSidebar, navigate, setShortcutsOpen]);

  const filteredCommands = useMemo(() => {
    if (!commandFilter) return commands;
    return commands.filter(
      (command) =>
        command.label.toLowerCase().includes(commandFilter) ||
        (command.keywords ?? '').includes(commandFilter),
    );
  }, [commands, commandFilter]);

  const commandGroups = useMemo(() => {
    const order: PaletteCommand['group'][] = ['Aktionen', 'Navigation', 'Ansicht'];
    return order
      .map((group) => ({ group, items: filteredCommands.filter((command) => command.group === group) }))
      .filter((section) => section.items.length > 0);
  }, [filteredCommands]);
  const groups = data?.data.groups ?? [];
  const flatHits = useMemo(() => groups.flatMap((group) => group.hits), [groups]);

  // Genaue Treffer bleiben gruppiert, ähnliche kommen flach in einen eigenen
  // Abschnitt UNTER die genauen (Tech-Lead 2026-08-12).
  const { exactGroups, similarHits } = useMemo(() => splitSearchGroups(groups), [groups]);

  // Kategorie-Chips: je Typ eine Pille mit ALLEN Treffern des Typs (genau +
  // ähnlich), in der Reihenfolge der Trefferliste.
  const chips = useMemo(() => {
    const byType = new Map<SearchResourceTypeUi, { label: string; count: number }>();
    for (const group of exactGroups) {
      byType.set(group.type, { label: group.label || RESOURCE_LABEL[group.type], count: group.hits.length });
    }
    for (const { hit, label } of similarHits) {
      const entry = byType.get(hit.type);
      if (entry) entry.count += 1;
      else byType.set(hit.type, { label, count: 1 });
    }
    return [...byType].map(([type, entry]) => ({ type, ...entry }));
  }, [exactGroups, similarHits]);

  // Chip-Filter (client-seitig, der Server liefert alle Typen).
  const filteredGroups = useMemo(
    () => (activeType === 'all' ? exactGroups : exactGroups.filter((group) => group.type === activeType)),
    [exactGroups, activeType],
  );
  const filteredSimilar = useMemo(
    () => (activeType === 'all' ? similarHits : similarHits.filter((entry) => entry.hit.type === activeType)),
    [similarHits, activeType],
  );

  // Angezeigter Ausschnitt + was dahinter noch liegt.
  const shownGroups = useMemo(
    () => (areAllGroupsShown ? filteredGroups : filteredGroups.slice(0, VISIBLE_GROUP_LIMIT)),
    [filteredGroups, areAllGroupsShown],
  );
  const shownSimilar = useMemo(
    () => (areAllSimilarShown ? filteredSimilar : filteredSimilar.slice(0, VISIBLE_SIMILAR_LIMIT)),
    [filteredSimilar, areAllSimilarShown],
  );
  const hiddenGroups = filteredGroups.slice(shownGroups.length);
  const hiddenSimilarCount = filteredSimilar.length - shownSimilar.length;
  const shownChips = areAllChipsShown ? chips : chips.slice(0, VISIBLE_CHIP_LIMIT);
  const hiddenChipCount = chips.length - shownChips.length;

  // Tatsächlich gerenderte Treffer — nur die kann cmdk markieren.
  const renderedHits = useMemo(
    () => [...shownGroups.flatMap((group) => group.hits), ...shownSimilar.map((entry) => entry.hit)],
    [shownGroups, shownSimilar],
  );
  const visibleHitCount = countHits(filteredGroups) + filteredSimilar.length;

  const hasQuery = debounced.trim().length >= 1;
  const hasResults = hasQuery && !isLoading && !isError && flatHits.length > 0;

  // Beim Öffnen mit leerer Eingabe, ohne Filter und ohne Markierung starten.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveType('all');
      setSelectedValue('');
    }
  }, [open]);

  // Chips und aufgeklappte Abschnitte zurücksetzen, sobald sich der (debouncte)
  // Suchbegriff ändert — sonst gilt der Aufklapp-Zustand einer alten Trefferliste.
  useEffect(() => {
    setActiveType('all');
    setAreAllChipsShown(false);
    setAreAllGroupsShown(false);
    setAreAllSimilarShown(false);
  }, [debounced]);

  // Markierung mit den gerenderten Treffern synchron halten: verschwindet der
  // markierte Eintrag (neue Suche, Chip-Wechsel), wird der erste Treffer markiert.
  useEffect(() => {
    if (commandMode) return; // im Befehls-Modus verwaltet cmdk die Markierung selbst
    const isSpecialRowVisible =
      (selectedValue === ALL_RESULTS_VALUE && hasResults) ||
      (selectedValue === MORE_GROUPS_VALUE && hiddenGroups.length > 0) ||
      (selectedValue === MORE_SIMILAR_VALUE && hiddenSimilarCount > 0);
    const isSelectionVisible =
      isSpecialRowVisible || renderedHits.some((hit) => hitValue(hit) === selectedValue);
    if (!isSelectionVisible) {
      setSelectedValue(renderedHits[0] ? hitValue(renderedHits[0]) : '');
    }
  }, [renderedHits, selectedValue, hasResults, commandMode, hiddenGroups.length, hiddenSimilarCount]);

  // Markierter Treffer für die Vorschau-Spalte (nur gerenderte Treffer zählen).
  const selectedHit = useMemo(
    () => renderedHits.find((hit) => hitValue(hit) === selectedValue) ?? null,
    [renderedHits, selectedValue],
  );
  const isAllResultsSelected = selectedValue === ALL_RESULTS_VALUE && hasResults;

  function go(hit: SearchHit) {
    navigate(featureRoute(hit.type, hit.id));
    onClose();
  }

  function goAll() {
    navigate(`/suche?q=${encodeURIComponent(debounced.trim())}`);
    onClose();
  }

  /** Chip-Klick: Kategorie umschalten und die Markierung auf den Erst-Treffer ziehen. */
  function filterByType(type: SearchResourceTypeUi | 'all') {
    setActiveType(type);
    const nextGroups = type === 'all' ? exactGroups : exactGroups.filter((group) => group.type === type);
    const nextSimilar =
      type === 'all' ? similarHits : similarHits.filter((entry) => entry.hit.type === type);
    const firstHit = nextGroups[0]?.hits[0] ?? nextSimilar[0]?.hit;
    setSelectedValue(firstHit ? hitValue(firstHit) : '');
  }

  /** „Mehr Kategorien": aufklappen und auf den ersten neu sichtbaren Treffer springen. */
  function showAllGroups() {
    setAreAllGroupsShown(true);
    const firstHidden = hiddenGroups[0]?.hits[0];
    if (firstHidden) setSelectedValue(hitValue(firstHidden));
  }

  /** „Mehr ähnliche Treffer": aufklappen und auf den ersten neu sichtbaren springen. */
  function showAllSimilar() {
    setAreAllSimilarShown(true);
    const firstHidden = filteredSimilar[shownSimilar.length]?.hit;
    if (firstHidden) setSelectedValue(hitValue(firstHidden));
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
      title="Universelle Suche"
      description="Projekte, Kunden, Artikel und Belege modulübergreifend durchsuchen"
      // Großes, zentrales Overlay: ~2/3 der Fensterbreite, hoch, füllende Trefferliste.
      className="top-[8vh] h-[80vh] max-h-[80vh] w-[94vw] max-w-[94vw] flex-col sm:max-w-[66vw]"
    >
      {/* Server filtert (ADR 0015) — cmdk-Filter deaktiviert. Markierung kontrolliert,
          damit die Vorschau-Spalte dem markierten Eintrag folgt. */}
      <Command
        shouldFilter={false}
        value={selectedValue}
        onValueChange={setSelectedValue}
        className="flex min-h-0 flex-1 flex-col"
      >
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder={`Suchen … oder mit ${COMMAND_PREFIX} Befehle aufrufen`}
          aria-label="Suchbegriff"
          className="h-12 text-base"
        />

        {/* Kategorie-Chips (Variante B): „Alle" + eine Pille je Ergebnis-Gruppe. */}
        {!commandMode && hasResults && (
          <div
            role="group"
            aria-label="Ergebnisse nach Kategorie filtern"
            className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-2"
          >
            <FilterChip
              label="Alle"
              count={flatHits.length}
              isActive={activeType === 'all'}
              onSelect={() => filterByType('all')}
            />
            {shownChips.map((chip) => (
              <FilterChip
                key={chip.type}
                label={chip.label}
                count={chip.count}
                isActive={activeType === chip.type}
                onSelect={() => filterByType(chip.type)}
              />
            ))}
            {hiddenChipCount > 0 && (
              <MoreChipsButton count={hiddenChipCount} onSelect={() => setAreAllChipsShown(true)} />
            )}
          </div>
        )}

        {commandMode ? (
          <CommandList className="max-h-full min-h-0 flex-1 overflow-y-auto">
            {filteredCommands.length === 0 && (
              <ListStatus>Kein Befehl für „{commandFilter}".</ListStatus>
            )}
            {commandGroups.map(({ group, items }) => (
              <CommandGroup key={group} heading={group}>
                {items.map((command) => {
                  const Icon = command.icon;
                  return (
                    <CommandItem
                      key={command.id}
                      value={command.id}
                      onSelect={() => {
                        command.run();
                        onClose();
                      }}
                      className="gap-3 py-2.5"
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <Icon className="!size-4" strokeWidth={1.5} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{command.label}</span>
                      {command.hint && (
                        <span className="shrink-0 text-xs text-muted-foreground">{command.hint}</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)] md:grid-cols-[1.15fr_1fr]">
          <CommandList className="max-h-full min-h-0 overflow-y-auto">
            {!hasQuery && (
              <ListStatus>
                <span className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Search className="size-5" strokeWidth={1.5} aria-hidden />
                </span>
                <span className="text-base font-medium text-foreground">Modulübergreifend suchen</span>
                <span>Projekte, Kunden, Artikel und Belege — einfach lostippen.</span>
              </ListStatus>
            )}
            {hasQuery && isLoading && (
              <ListStatus>
                <Spinner aria-hidden />
                Suche läuft …
              </ListStatus>
            )}
            {hasQuery && isError && (
              <ListStatus tone="danger">Suche fehlgeschlagen. Bitte erneut versuchen.</ListStatus>
            )}
            {hasQuery && !isLoading && !isError && flatHits.length === 0 && (
              <ListStatus>Keine Treffer für „{debounced}".</ListStatus>
            )}

            {shownGroups.map((group) => {
              const Icon = RESOURCE_ICON[group.type];
              return (
                <CommandGroup key={group.type} heading={group.label}>
                  {group.hits.map((hit) => (
                    <CommandItem
                      key={hitValue(hit)}
                      value={hitValue(hit)}
                      onSelect={() => go(hit)}
                      className="gap-3 py-2.5"
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <Icon className="!size-4" strokeWidth={1.5} aria-hidden />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm text-foreground">{hit.title}</span>
                        {hit.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">{hit.subtitle}</span>
                        )}
                      </span>
                      {hit.badge && <Badge>{hit.badge}</Badge>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}

            {/* Weggelassene Kategorien — eine Zeile statt 20 Überschriften. */}
            {hiddenGroups.length > 0 && (
              <CommandGroup>
                <CommandItem value={MORE_GROUPS_VALUE} onSelect={showAllGroups} className="gap-3 py-2.5">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <ChevronDown className="!size-4" strokeWidth={1.5} aria-hidden />
                  </span>
                  <span className="flex-1 text-sm text-foreground">
                    {plural(hiddenGroups.length, 'weitere Kategorie', 'weitere Kategorien')} mit{' '}
                    {plural(countHits(hiddenGroups), 'Treffer', 'Treffern')} anzeigen
                  </span>
                </CommandItem>
              </CommandGroup>
            )}

            {/* Ähnliche Treffer: eigener Abschnitt UNTER den genauen Treffern. */}
            {shownSimilar.length > 0 && (
              <>
                <CommandSeparator className="mx-2 my-1" />
                {/* Überschrift als cmdk-`heading`, damit die Gruppe sie auch
                    vorgelesen bekommt (aria-labelledby) — nicht als loser Text. */}
                <CommandGroup
                  heading={
                    <span className="flex flex-col gap-0.5 pt-1">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                        <Sparkles className="size-3.5 text-muted-foreground" strokeWidth={1.5} aria-hidden />
                        Ähnliche Treffer
                      </span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {countHits(exactGroups) > 0
                          ? 'Nicht genau dein Suchbegriff — ähnlich geschrieben (Tippfehler oder andere Schreibweise).'
                          : `Nichts stimmt genau mit „${debounced.trim()}" überein. Diese Datensätze sind ähnlich geschrieben.`}
                      </span>
                    </span>
                  }
                >
                  {shownSimilar.map(({ hit, label }) => {
                    const Icon = RESOURCE_ICON[hit.type];
                    return (
                      <CommandItem
                        key={hitValue(hit)}
                        value={hitValue(hit)}
                        onSelect={() => go(hit)}
                        className="gap-3 py-2.5"
                      >
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <Icon className="!size-4" strokeWidth={1.5} aria-hidden />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm text-foreground">{hit.title}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {[label, hit.subtitle].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <SimilarBadge hit={hit} />
                      </CommandItem>
                    );
                  })}
                  {hiddenSimilarCount > 0 && (
                    <CommandItem value={MORE_SIMILAR_VALUE} onSelect={showAllSimilar} className="gap-3 py-2.5">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <ChevronDown className="!size-4" strokeWidth={1.5} aria-hidden />
                      </span>
                      <span className="flex-1 text-sm text-foreground">
                        {plural(hiddenSimilarCount, 'weiteren ähnlichen Treffer', 'weitere ähnliche Treffer')}{' '}
                        anzeigen
                      </span>
                    </CommandItem>
                  )}
                </CommandGroup>
              </>
            )}

            {hasResults && (
              <CommandGroup>
                <CommandItem value={ALL_RESULTS_VALUE} onSelect={goAll} className="gap-3 py-2.5">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                    <ArrowRight className="!size-4" strokeWidth={1.5} aria-hidden />
                  </span>
                  <span className="flex-1 text-sm text-foreground">
                    Alle Ergebnisse für „{debounced.trim()}" anzeigen
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>

          {/* Vorschau-Spalte: nur ab `md` sichtbar, darunter einspaltig. */}
          <aside aria-label="Detailvorschau" className="hidden min-h-0 border-l border-border md:block">
            {selectedHit ? (
              <HitPreview
                hit={selectedHit}
                groupLabel={
                  chips.find((chip) => chip.type === selectedHit.type)?.label ??
                  RESOURCE_LABEL[selectedHit.type]
                }
                onOpen={() => go(selectedHit)}
              />
            ) : isAllResultsSelected ? (
              <AllResultsPreview query={debounced.trim()} onOpen={goAll} />
            ) : (
              <PreviewPlaceholder />
            )}
          </aside>
        </div>
        )}

        {/* Fuß: Tastatur-Hinweise + Trefferzahl. */}
        <div className="flex items-center justify-between gap-4 border-t border-border px-3 py-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> navigieren</span>
            <span className="flex items-center gap-1"><Kbd>↵</Kbd> öffnen</span>
            <span className="flex items-center gap-1"><Kbd>{COMMAND_PREFIX}</Kbd> Befehle</span>
            <span className="flex items-center gap-1"><Kbd>esc</Kbd> schließen</span>
          </span>
          {hasResults && (
            <span className="tabular-nums">
              {activeType === 'all'
                ? `${flatHits.length} Treffer`
                : `${visibleHitCount} von ${flatHits.length} Treffern`}
              {similarHits.length > 0 && ` · ${similarHits.length} ähnlich`}
            </span>
          )}
        </div>
      </Command>
    </CommandDialog>
  );
}
